/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  PageLayout,
  TableSkeleton,
  ProductModal,
  ParameterModal,
  emptyProductIllustration,
  SuccessToast,
  ProductCategories,
  ProductsList,
  EmptyState,
  InventoryCatalogHeader,
  CatalogDeleteConfirmDialog,
} from "@components";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useCache } from "../../context/CacheContext";
import { useParameters, useTimedToast, useCatalogViewMode } from "../../hooks";
import { useWorkspaceData } from "@data";
import { useTranslation } from "react-i18next";
import { ParameterType, IProduct } from "../../types";
import { Parameter, Product } from "../../models";

export const ProductsPage: FC = function () {
  const [isLoading, setLoading] = useState(true);
  const [splitIsOpen, setSplitIsOpen] = useState(false);
  const { updateCacheKey } = useCache();

  const [isUpsertModalOpen, openUpsertModal] = useState(false);
  const [isDeleteModalOpen, openDeleteModal] = useState(false);
  const [isCategoryModalOpen, setCategoryModalOpen] = useState(false);
  const { activeWorkspace } = useWorkspace();
  const [productData, setProductData] = useState(Product.default());
  const [productCategories, setProductCategories] = useState([] as Parameter[]);
  const { viewMode, setViewMode } = useCatalogViewMode("grid");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  const {
    show: showProductSuccessToast,
    message: successToastMessage,
    showToast,
    hideToast,
  } = useTimedToast(3000);

  const categoriesQuery = useParameters(undefined, ParameterType.ProductType);
  // Products come from the centrally-loaded workspace data store.
  const { products, loading } = useWorkspaceData();
  const { t } = useTranslation(["common", "inventory", "products"]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const clearNewParam = () => {
    if (searchParams.get("new") === "true") {
      navigate(window.location.pathname, { replace: true });
    }
  };

  const isQueryLoading = useMemo(
    () =>
      loading.products ||
      categoriesQuery.isLoading ||
      categoriesQuery.isRefetching,
    [loading.products, categoriesQuery],
  );

  const categories = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery],
  );

  useEffect(() => {
    if (!isQueryLoading) {
      setProductCategories(categories);
    }
  }, [isQueryLoading, categories]);

  useEffect(() => {
    if (!isQueryLoading) {
      setLoading(false);
    }
  }, [isQueryLoading, products]);

  // Open the create modal when arriving via the setup checklist (?new=true).
  useEffect(() => {
    if (searchParams.get("new") === "true" && !isQueryLoading) {
      setProductData(Product.default());
      openUpsertModal(true);
    }
  }, [searchParams, isQueryLoading]);

  const handleSelectProduct = (product: Product) => {
    setProductData(product);
    if (Product.isNew(product)) {
      openUpsertModal(true);
    } else if (viewMode === "grid") {
      openUpsertModal(true);
    } else {
      setSplitIsOpen(true);
    }
  };

  const handleCreateProduct = () => {
    setProductData(Product.default());
    openUpsertModal(true);
  };

  const handleAddCategory = () => {
    setCategoryModalOpen(true);
  };

  const handleDeleteCategory = (categoryId: string) => {
    const categoryToDelete = categories.find((c) => c.id === categoryId);
    if (!categoryToDelete) return;

    // Slett kategorien direkte uten bekreftelsesdialog (bekreftelsen skjer allerede i modal)
    Parameter.remove(categoryToDelete);
    updateCacheKey();
    setLoading(true);

    // Show success toast for category deletion
    showToast(
      t("products:toast.category_deleted", { category: categoryToDelete.code }),
    );

    if (selectedCategoryId === categoryId) {
      setSelectedCategoryId(null);
    }
  };

  return (
    <PageLayout>
      {isLoading ? (
        <TableSkeleton />
      ) : products.length === 0 ? (
        // Render empty state when there are no products
        <EmptyState
          icon={emptyProductIllustration}
          title={t("products:empty_state.title")}
          description={t("products:empty_state.description")}
          buttonText={t("products:empty_state.button_text")}
          buttonClick={handleCreateProduct}
        />
      ) : (
        <div className="space-y-4">
          {/* Header with view toggle */}
          <InventoryCatalogHeader
            title={t("products:title")}
            description={t("products:description")}
            helpStorySlug="logibud-tms-web/help-popups/products"
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            gridViewTitle={t("products:view.grid")}
            listViewTitle={t("products:view.list")}
            createButtonText={t("products:new_product")}
            onCreate={handleCreateProduct}
            createButtonClassName="font-medium w-full sm:w-auto border-border-subtle dark:border dark:border-gray-500"
            createButtonIconClassName="mr-2 h-5 w-5 dark:text-white"
          />

          {/* Content based on view mode */}
          {viewMode === "grid" ? (
            <div className="p-4">
              <ProductCategories
                products={products}
                categories={productCategories}
                isLoading={isQueryLoading}
                onSelectCategory={setSelectedCategoryId}
                onSelectProduct={handleSelectProduct}
                selectedCategoryId={selectedCategoryId}
                onAddCategory={handleAddCategory}
                onDeleteCategory={handleDeleteCategory}
              />
            </div>
          ) : (
            <ProductsList
              products={products}
              categories={productCategories}
              isLoading={isQueryLoading}
              isOpen={setSplitIsOpen}
              createOrUpdate={(formData: Product) => {
                setProductData(formData);
                openUpsertModal(true);
              }}
            />
          )}
        </div>
      )}

      {/* Product Create/Edit Modal */}
      <ProductModal
        key={productData.id || "new"}
        data={productData}
        productTypes={productCategories}
        isShowing={isUpsertModalOpen}
        onConfirm={async (formData: IProduct) => {
          const isNew = Product.isNew(productData);
          try {
            if (isNew) {
              const created = await Product.create(
                activeWorkspace?.workspaceId ?? "",
                formData,
              );
              if (!created) return;
            } else {
              await Product.update(productData, formData);
            }
          } catch (error) {
            console.error("Error saving product:", error);
            return;
          }

          openUpsertModal(false);
          clearNewParam();
          updateCacheKey();
          setLoading(true);

          // Show success toast with appropriate message
          showToast(
            isNew
              ? t("products:toast.product_created", {
                  product: formData.descriptionShort,
                })
              : t("products:toast.product_updated", {
                  product: formData.descriptionShort,
                }),
          );
        }}
        onCancel={() => {
          openUpsertModal(false);
          clearNewParam();
        }}
      />

      {/* Product Delete Confirmation */}
      <CatalogDeleteConfirmDialog
        title={
          t("inventory:delete_product") +
          " " +
          productData.descriptionShort +
          "?"
        }
        show={isDeleteModalOpen}
        onToggle={() => openDeleteModal(false)}
        onConfirm={() => {
          Product.remove(productData);
          openDeleteModal(false);
          updateCacheKey();
          setLoading(true);

          // Show success toast for product deletion
          showToast(
            t("products:toast.product_deleted", {
              product: productData.descriptionShort,
            }),
          );
        }}
      />

      {/* Category Create Modal */}
      <ParameterModal
        data={Parameter.default()}
        predefinedType={ParameterType.ProductType}
        isShowing={isCategoryModalOpen}
        onConfirm={(formData) => {
          Parameter.create(activeWorkspace?.workspaceId ?? "", formData);
          setCategoryModalOpen(false);
          updateCacheKey();
          setLoading(true);

          // Show success toast for category creation
          showToast(
            t("products:toast.category_created", { category: formData.code }),
          );
        }}
        onCancel={() => setCategoryModalOpen(false)}
      />

      {/* Product Success Toast */}
      <SuccessToast
        title={t("common:success")}
        message={successToastMessage}
        show={showProductSuccessToast}
        onClose={hideToast}
        position="top-right"
      />
    </PageLayout>
  );
};
